import type { AppType, ShopPlatformStatus } from '~/types/graphql';

import { useShopPlatformsQuery } from '~/api/graphql/queries/shopPlatforms.generated';
import { captureSentryError } from '~/exceptions/sentry';
import { useShopStore } from '~/stores/shop';

// Keys mirror their values, so `Record<AppType, AppType>` keeps this map exhaustive:
// a member added to (or removed from) the generated union breaks the build here.
export const IPlatformAppType = {
  CUSTOM: 'CUSTOM',
  GEMPAGES: 'GEMPAGES',
  GEMPAGESV7: 'GEMPAGESV7',
  GEMUPSELL: 'GEMUPSELL',
  GEMX: 'GEMX',
  GEMXP: 'GEMXP',
} as const satisfies Record<AppType, AppType>;
export type IPlatformAppType = (typeof IPlatformAppType)[keyof typeof IPlatformAppType];

export const ShopPlatformStatusEnum = {
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  NOT_CONNECTED: 'NOT_CONNECTED',
} as const satisfies Record<ShopPlatformStatus, ShopPlatformStatus>;
export type ShopPlatformStatusEnum =
  (typeof ShopPlatformStatusEnum)[keyof typeof ShopPlatformStatusEnum];

export const useQueryShopPlatform = () => {
  const shopifyDomain = useShopStore((state) => state.shopInfo?.shopifyDomain);
  const setShopPlatformGemPages = useShopStore((state) => state.setShopPlatformGemPages);
  const setShopPlatformGemX = useShopStore((state) => state.setShopPlatformGemX);
  const shopPlatformGemPages = useShopStore((state) => state.shopPlatformGemPages);
  const shopPlatformGemX = useShopStore((state) => state.shopPlatformGemX);

  useShopPlatformsQuery(
    {
      first: 10,
      orderBy: { direction: 'DESC', field: 'UPDATED_AT' },
      where: {
        appTypeIn: [IPlatformAppType.GEMPAGESV7, IPlatformAppType.GEMXP],
        platformDomain: shopifyDomain,
      },
    },
    {
      enabled: (!shopPlatformGemPages?.status || !shopPlatformGemX?.status) && !!shopifyDomain,
      onSuccess: (data) => {
        const nodes = data.shopPlatforms?.edges?.map((edge) => edge?.node) ?? [];

        setShopPlatformGemPages(
          nodes.find((node) => node?.appType === IPlatformAppType.GEMPAGESV7) ?? null,
        );
        setShopPlatformGemX(nodes.find((node) => node?.appType === IPlatformAppType.GEMXP) ?? null);
      },
      onError: (error) => {
        captureSentryError(error, {
          source: 'useQueryShopPlatform',
          tags: { app: 'App' },
        });
      },
    },
  );

  const isAlreadyInstalledGemPages = shopPlatformGemPages !== null;
  const isInstallGemPages = shopPlatformGemPages?.status === ShopPlatformStatusEnum.CONNECTED;

  return {
    isInstallGemPages,
    isAlreadyInstalledGemPages,
  };
};
